import { useMutation } from '@apollo/client';
import { SetOrganisationWebhookStatusDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { SetWebhookStatusInput } from './useUpdateWebhookStatusTRPC';
import { useUpdateWebhookStatusTRPC } from './useUpdateWebhookStatusTRPC';

/**
 * Flag-gated wrapper: tRPC `webhook.setStatus`, or the v2
 * `setOrganisationWebhookStatusApi` action on the legacy path.
 */
export const useUpdateWebhookStatus = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [setStatusGraphQL, graphqlState] = useMutation(
    SetOrganisationWebhookStatusDocument,
    {
      update: (cache) => {
        evictField(cache, 'organisation_webhook');
      },
    }
  );

  const trpcMutation = useUpdateWebhookStatusTRPC();

  const updateWebhookStatus = async (
    input: SetWebhookStatusInput
  ): Promise<void> => {
    if (trpcEnabled) {
      await trpcMutation.setStatus(input);

      return;
    }

    const result = await setStatusGraphQL({
      variables: { object: { Id: input.id, Status: input.status } },
    });
    if (!result.data) {
      throw new Error('Failed to update webhook status');
    }
  };

  if (trpcEnabled) {
    return {
      updateWebhookStatus,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateWebhookStatus,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
